import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, STRATEGY_TAGS, WHY_BUY, WHY_SELL } from "@/lib/journal";
import { WhyPicker } from "@/components/WhyPicker";

export const EditNotesDialog = ({ trade, onOpenChange, onSaved, portfolios = [] }) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [whyBuy, setWhyBuy] = useState("");
  const [whySell, setWhySell] = useState("");
  const [portfolioId, setPortfolioId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (trade) {
      setReason(trade.reason || "");
      setNotes(trade.closing_notes || "");
      setTags(trade.tags || []);
      setWhyBuy(trade.why_buy || "");
      setWhySell(trade.why_sell || "");
      setPortfolioId(trade.portfolio_id || "");
    }
  }, [trade]);

  if (!trade) return null;

  const toggle = (t) => setTags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/trades/${trade.id}`, {
        reason: reason || " ",
        tags,
        ...(whyBuy ? { why_buy: whyBuy } : {}),
        ...(trade.status === "closed" && whySell ? { why_sell: whySell } : {}),
        ...(portfolioId && portfolioId !== trade.portfolio_id ? { portfolio_id: portfolioId } : {}),
        ...(trade.status === "closed" ? { closing_notes: notes || " " } : {}),
      });
      toast.success(
        portfolioId && portfolioId !== trade.portfolio_id
          ? "Trade moved and notes updated"
          : "Trade notes updated"
      );
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!trade} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-notes-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Edit Notes <span className="text-[#C58361]">/ {trade.asset}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Refine the thesis and tags without touching the numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {portfolios.length > 1 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Portfolio</Label>
              <Select value={portfolioId} onValueChange={setPortfolioId}>
                <SelectTrigger data-testid="edit-portfolio-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`edit-portfolio-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="font-mono text-[10px] text-zinc-600">Moving a trade keeps all of its numbers intact.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Why Buy?</Label>
            <WhyPicker options={WHY_BUY} value={whyBuy} onChange={setWhyBuy} testPrefix="edit-why-buy" />
          </div>

          {trade.status === "closed" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Why Sell?</Label>
              <WhyPicker options={WHY_SELL} value={whySell} onChange={setWhySell} testPrefix="edit-why-sell" />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Reason for Purchase</Label>
            <Textarea
              data-testid="edit-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
            />
          </div>

          {trade.status === "closed" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Closing Thoughts</Label>
              <Textarea
                data-testid="edit-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Strategy Tags</Label>
            <div className="flex flex-wrap gap-2">
              {[...new Set([...STRATEGY_TAGS, ...tags])].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`edit-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => toggle(t)}
                  className={`border px-2.5 py-1 text-xs transition-colors duration-200 ${
                    tags.includes(t)
                      ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
                      : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="edit-save-btn"
            onClick={save}
            disabled={saving}
            className="w-full rounded-none bg-[#C58361] py-6 font-medium text-black hover:bg-[#A86B4C]"
          >
            {saving ? "Saving…" : "Save Notes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
